import { useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import { CardComp, TableCompData } from "../../../../components";
import TableSkeletonLoader from "../../../../components/Common/TableSkeletonLoader";
import ConfirmationModal from "../../../../components/Common/ConfirmationModal";
import AdminTestimonialModal from "../../../../components/Admin/InvestorPortal/AdminTestimonialModal";
import InvestorPortalPageHeader, {
  InvestorPortalPrimaryButton,
} from "../../../../components/Admin/InvestorPortal/InvestorPortalPageHeader";
import InvestorPortalTableEmpty from "../../../../components/Admin/InvestorPortal/InvestorPortalTableEmpty";
import InvestorPortalTableSection from "../../../../components/Admin/InvestorPortal/InvestorPortalTableSection";
import TableActionDropdown from "../../../../components/Admin/InvestorPortal/TableActionDropdown";
import {
  useListStaffWebsiteTestimonialsQuery,
  useCreateStaffWebsiteTestimonialMutation,
  useUpdateStaffWebsiteTestimonialMutation,
  useDeleteStaffWebsiteTestimonialMutation,
  usePublishStaffWebsiteTestimonialMutation,
  useUnpublishStaffWebsiteTestimonialMutation,
} from "../../../../store/admin/investorPortal/staffWebsiteTestimonialsApi";
import { withInvestorPortalGuard } from "../withInvestorPortalGuard";

function TestimonialsPage() {
  const [typeFilter, setTypeFilter] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const { data, isFetching, isError, refetch } = useListStaffWebsiteTestimonialsQuery(
    typeFilter ? { type: typeFilter } : {}
  );
  const [createItem] = useCreateStaffWebsiteTestimonialMutation();
  const [updateItem] = useUpdateStaffWebsiteTestimonialMutation();
  const [deleteItem] = useDeleteStaffWebsiteTestimonialMutation();
  const [publishItem] = usePublishStaffWebsiteTestimonialMutation();
  const [unpublishItem] = useUnpublishStaffWebsiteTestimonialMutation();

  const items = useMemo(() => data?.items || [], [data]);

  const openCreate = () => {
    setEditing(null);
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setModalOpen(true);
  };

  const handleSubmit = async (values) => {
    try {
      if (editing?.id) {
        await updateItem({ id: editing.id, values }).unwrap();
        toast.success("Testimonial updated");
      } else {
        await createItem(values).unwrap();
        toast.success("Testimonial created");
      }
    } catch (error) {
      toast.error(error?.data?.message || "Unable to save testimonial.");
      throw error;
    }
  };

  const columns = [
    {
      name: "Type",
      cell: (row) => (
        <span
          className="badge rounded-pill px-3 py-2 text-white"
          style={{ backgroundColor: row.type === "video" ? "#31778c" : "#8b313a", minWidth: 90 }}
        >
          {row.type === "video" ? "Video" : "Written"}
        </span>
      ),
      minWidth: "110px",
    },
    { name: "Name", selector: (row) => row.name, minWidth: "160px", grow: 1 },
    {
      name: "Role",
      selector: (row) => row.role || "—",
      minWidth: "140px",
      grow: 1,
    },
    {
      name: "Published",
      cell: (row) => (
        <span
          className="badge rounded-pill px-3 py-2 text-white"
          style={{ backgroundColor: row.isPublished ? "#19BC31" : "#A1A0A0", minWidth: 90 }}
        >
          {row.isPublished ? "Published" : "Draft"}
        </span>
      ),
      minWidth: "120px",
    },
    {
      name: "Featured",
      cell: (row) => (row.type === "written" ? (row.isFeatured ? "Yes" : "No") : "—"),
      minWidth: "100px",
      center: true,
    },
    { name: "Sort", selector: (row) => row.sortOrder ?? 0, minWidth: "80px", center: true },
    {
      name: "Action",
      cell: (row) => (
        <TableActionDropdown
          items={[
            { name: "Edit", action: () => openEdit(row) },
            {
              name: row.isPublished ? "Unpublish" : "Publish",
              action: () => {
                setConfirm({
                  title: row.isPublished ? "Unpublish testimonial" : "Publish testimonial",
                  message: `${row.isPublished ? "Unpublish" : "Publish"} "${row.name}"?`,
                  confirmText: row.isPublished ? "Unpublish" : "Publish",
                  confirmButtonClass: row.isPublished ? "bg-danger text-white" : "bg-primary1 text-white",
                  action: async () => {
                    try {
                      if (row.isPublished) {
                        await unpublishItem(row.id).unwrap();
                        toast.success("Unpublished");
                      } else {
                        await publishItem(row.id).unwrap();
                        toast.success("Published");
                      }
                    } catch (error) {
                      toast.error(error?.data?.message || "Unable to update status.");
                    }
                    setConfirm(null);
                  },
                });
              },
            },
            {
              name: "Delete",
              action: () => {
                setConfirm({
                  title: "Delete testimonial",
                  message: `Delete "${row.name}"? This cannot be undone.`,
                  confirmText: "Delete",
                  confirmButtonClass: "bg-danger text-white",
                  action: async () => {
                    try {
                      await deleteItem(row.id).unwrap();
                      toast.success("Deleted");
                    } catch (error) {
                      toast.error(error?.data?.message || "Unable to delete.");
                    }
                    setConfirm(null);
                  },
                });
              },
            },
          ]}
        />
      ),
      minWidth: "100px",
      ignoreRowClick: true,
    },
  ];

  return (
    <div className="pb-4">
      <InvestorPortalPageHeader
        title="Website testimonials"
        subtitle="Manage written and video testimonials shown on your public website."
        action={<InvestorPortalPrimaryButton btnText="Add testimonial" onClick={openCreate} />}
      />

      <CardComp cardClassName="border-0 shadow-sm mb-3">
        <div className="d-flex flex-wrap gap-2 align-items-center">
          <label className="small text-muted mb-0">Filter by type</label>
          <select
            className="form-select form-select-sm"
            style={{ maxWidth: 200 }}
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <option value="">All</option>
            <option value="written">Written</option>
            <option value="video">Video</option>
          </select>
        </div>
      </CardComp>

      <InvestorPortalTableSection>
        {isError ? (
          <InvestorPortalTableEmpty
            title="Could not load testimonials"
            description="Refresh and try again."
            actionLabel="Retry"
            onAction={() => refetch()}
          />
        ) : (
          <TableCompData
            columns={columns}
            data={items}
            progressPending={isFetching}
            progressComponent={<TableSkeletonLoader rows={8} columns={7} />}
            noDataComponent={
              <InvestorPortalTableEmpty
                title="No testimonials yet"
                description="Add written or video stories for your public website."
                actionLabel="Add testimonial"
                onAction={openCreate}
              />
            }
          />
        )}
      </InvestorPortalTableSection>

      <AdminTestimonialModal
        show={modalOpen}
        testimonial={editing}
        onClose={() => {
          setModalOpen(false);
          setEditing(null);
        }}
        onSubmit={handleSubmit}
      />

      <ConfirmationModal
        isOpen={Boolean(confirm)}
        onClose={() => setConfirm(null)}
        onConfirm={confirm?.action}
        title={confirm?.title}
        message={confirm?.message}
        confirmText={confirm?.confirmText || "Confirm"}
        cancelText="Cancel"
        confirmButtonClass={confirm?.confirmButtonClass || "bg-primary1 text-white"}
      />
    </div>
  );
}

export default withInvestorPortalGuard(TestimonialsPage, "investorPortal");
